import { get , isEmpty ,  isNil, isEqual } from 'lodash';
import { ConditionalOperator } from '@ispm/shared/constants/conditional-operator.enum';
import { FormulaService } from '@ispm/shared/services/formula.service';

export class ConditionalItem<RESOURCE= any> implements IConditionalItem {
  public value: any;
  public operator: any;
  public path: string;
  public formula: string;
  protected resourceValue?;

  public static builder(
    resource: any,
    value: any,
    operator: ConditionalOperator,
    path?: string,
    formula?: string
  ) {
    return new this(resource, value, operator, path, formula);
  }

  public static from(resource: any, conditionalItem: ConditionalItem | IConditionalItem) {
    return this.builder(resource, conditionalItem.value, conditionalItem.operator, conditionalItem.path, conditionalItem.formula);
  }

  constructor(
    resource: RESOURCE,
    value: any,
    operator: ConditionalOperator,
    path?: string,
    formula?: string
  ) {
    this.resourceValue = path ? get(resource, path, null) : resource;
    this.value = value;
    this.operator = operator;
    this.formula = formula;
  }

  public setValue(value) {
    this.value = value;
    return this;
  }

  verify() {
    if (this.formula && this.resourceValue) {
      //For some reason 'evaluateFormula' always returns false when getting a string.
      //So, instead of understanding all the scenarios and usages of 'evaluateFormula', doing this non-elegant fix and passing an array with a single string instead of the string itself.      
      const valueToValidate = typeof this.resourceValue === 'string' ? [this.resourceValue] : this.resourceValue; 
      return FormulaService.evaluateFormula(this.formula, valueToValidate, false );
    }

    switch (this.operator && this.operator.toUpperCase()) {
      case ConditionalOperator.EQ:
        return this._equalCheck();
      case ConditionalOperator.NEQ:
        return !this._equalCheck();
      case ConditionalOperator.LT:
        return this._lowerCheck();
      case ConditionalOperator.LE:
        return this._lowerEqualCheck();
      case ConditionalOperator.GT:
        return this._greaterCheck();
      case ConditionalOperator.GE:
        return this._greaterEqualCheck();
      case ConditionalOperator.CONTAINS:
        return this._containsCheck();
      case ConditionalOperator.NOT_CONTAINS:
        return !this._containsCheck();
      case ConditionalOperator.DEFINED:
        return this._definedCheck();
      case ConditionalOperator.UNDEFINED:
        return !this._definedCheck();
      case ConditionalOperator.ONE_OF:
        return this._oneOfCheck();
      case ConditionalOperator.HAS_VALUE:
        return this._hasValue();
      case ConditionalOperator.NO_VALUE:
        return this._noValue();
      case ConditionalOperator.NOT_HAS_VALUE:
        return !this._hasValue();
      case ConditionalOperator.ALL_KEYS_ARE_EMPTY:
        return this._allKeyValuesAreEmpty();
      case ConditionalOperator.NOT_ALL_KEYS_ARE_EMPTY:
        return !this._allKeyValuesAreEmpty();
      default:
        return true;
    }
  }

  private _equalCheck() {
    return isEqual(this.resourceValue, this.value);
  }

  private _lowerCheck() {
    return this.resourceValue <  this.value;
  }

  private _lowerEqualCheck() {
    return this.resourceValue <=  this.value;
  }

  private _greaterCheck() {
    return this.resourceValue >  this.value;
  }

  private _greaterEqualCheck() {
    return this.resourceValue >=  this.value;
  }

  private _oneOfCheck() {
    return Array.isArray(this.value) && this.value.indexOf(this.resourceValue) > -1;
  }

  private _containsCheck() {
    if(Array.isArray(this.resourceValue)) {
      return this.resourceValue.includes(this.value);
    }
    return typeof this.resourceValue === 'string' && (<string> this.resourceValue).indexOf(this.value) > -1;
  }

  private _definedCheck() {
    return this.resourceValue !== undefined;
  }

  private _hasValue() {
    return !isNil(this.resourceValue) && !isEmpty(this.resourceValue);
  }

  private _noValue() {
    return isNil(this.resourceValue) || isEmpty(this.resourceValue);
  }

  private _allKeyValuesAreEmpty() {
    return this._noValue() ?  true : Object.values(this.resourceValue).every(val => isEmpty(val));
  }
}

export interface IConditionalItem {
  value: any;
  operator:  ConditionalOperator;
  path: string;
  formula?: string;
}

